<template>
    <PerspectiveWheelTransition :letters="resultLetters" :visible="showWheel" @done="onWheelDone" />
    <PerspectiveSurvey ref="survey" :questions="questions" :saved-responses="saved_responses" :session-id="session_id" sync-url @submitted="onSubmitted">
        <template #escape>
            <button class="inline-flex items-center gap-1.5 body-small text-gray-400 hover:text-gray-600 hover:underline transition-colors" @click="showCancelConfirm = true">
                <i class="bi bi-x-circle" aria-hidden="true"></i>
                Cancel Assessment
            </button>
        </template>
    </PerspectiveSurvey>
    <ConfirmationDialog
        v-model:open="showCancelConfirm"
        title="Cancel Assessment?"
        text="Your responses won't be saved and you'll need to start over."
        confirm-label="Cancel Assessment"
        cancel-label="Keep Going"
        @confirm="executeCancel" />
    <PerspectiveDemoTools :session-id="session_id" @filled="handleFilled" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import PerspectiveWheelTransition from "~vue/components/PerspectiveWheelTransition.vue";
import type { Question } from "~vue/pages/perspectiveTypes";
import PerspectiveDemoTools from "~vue/Perspective/PerspectiveDemoTools.vue";
import PerspectiveSurvey from "~vue/Perspective/PerspectiveSurvey.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { analyticsSourceFromQuery, logUserInteraction } from "~vue/utils/logUtils";
import { ref, useTemplateRef } from "vue";

export interface Props {
    questions: Question[];
    saved_responses: Record<string, number>;
    session_id: string;
}

const props = defineProps<Props>();

// Deep links into the assessment (e.g. the Teams feature nudge via /perspective/start/)
// carry ?source=<slug> through the redirect; keep it on the completion event so Amplitude
// can attribute the finished assessment to the surface that drove it.
const source = analyticsSourceFromQuery("perspective_assessment");

const survey = useTemplateRef<InstanceType<typeof PerspectiveSurvey>>("survey");
const showCancelConfirm = ref(false);

// ── Wheel animation ──
const showWheel = ref(false);
const resultLetters = ref<string | null>(null);

async function onSubmitted(payload: unknown) {
    await logUserInteraction(AmplitudeEventNames.PERSPECTIVE_ASSESSMENT_COMPLETED, { type: "perspective", source });
    resultLetters.value = (payload as { letters?: string } | null)?.letters ?? null;
    showWheel.value = true;
}

function onWheelDone() {
    router.visit("/perspective/");
}

const { execute: executeCancel, onFetchResponse: onCancelResponse } = useFetch(`/v1/perspectives/sessions/${props.session_id}`, { immediate: false }).delete();

onCancelResponse(() => {
    router.visit("/perspective/");
});

function handleFilled(filled: Record<string, number>) {
    survey.value?.adoptResponses(filled);
}
</script>
